import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Play, ExternalLink, Youtube } from 'lucide-react';
import { embedUrl, watchUrl, thumbUrl } from '../data/videos';

// Card used across the site.
// - Thumbnail from YouTube (real).
// - "Watch video" plays the embed inline.
// - "Watch on YouTube" opens the EXACT individual video URL in a new tab.
export const YouTubeVideoCard = ({ video, showCategory = true, showService = true, compact = false }) => {
  const [playing, setPlaying] = useState(false);
  const testKey = video.slug || video.youtubeId;

  return (
    <article
      data-testid={`video-card-${testKey}`}
      className="group bg-slate-900/60 border border-cyan-500/15 hover:border-cyan-400/40 rounded-2xl overflow-hidden transition-all duration-300 flex flex-col"
    >
      <div className="relative aspect-video bg-slate-950 overflow-hidden">
        {playing ? (
          <iframe
            src={embedUrl(video.youtubeId) + '&autoplay=1'}
            title={video.title}
            className="w-full h-full"
            frameBorder="0"
            allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <button
            data-testid={`play-btn-${testKey}`}
            onClick={() => setPlaying(true)}
            className="w-full h-full relative group/thumb"
            aria-label={`Play ${video.title}`}
          >
            <img
              src={thumbUrl(video.youtubeId)}
              alt={video.title}
              loading="lazy"
              className="w-full h-full object-cover group-hover/thumb:scale-105 transition-transform duration-500"
              onError={(e) => { e.currentTarget.style.opacity = '0'; }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-transparent to-transparent" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-slate-950/70 border border-cyan-400/50 flex items-center justify-center group-hover/thumb:scale-110 transition-transform">
                <Play className="w-6 h-6 text-cyan-300 fill-cyan-300 ml-1" />
              </div>
            </div>
          </button>
        )}
      </div>

      <div className="p-5 flex-1 flex flex-col">
        {showCategory && video.category && (
          <span className="text-[11px] uppercase tracking-widest text-cyan-400/80 mb-2">
            {video.category}
          </span>
        )}
        <h3 className={`font-semibold text-white leading-snug mb-2 ${compact ? 'text-sm sm:text-base' : 'text-base sm:text-lg'}`}>
          <Link
            to={`/the-cyber-guy/${video.slug || video.youtubeId}`}
            className="hover:text-cyan-200"
            data-testid={`video-title-${testKey}`}
          >
            {video.title}
          </Link>
        </h3>
        {!compact && video.description && (
          <p className="text-sm text-slate-300/80 leading-relaxed flex-1">
            {video.description}
          </p>
        )}
        {showService && video.service && (
          <div className="mt-3 text-xs text-slate-400">
            Related service:{' '}
            <Link
              to={`/services/${{
                'Security Assessment': 'security-assessment',
                'Vulnerability Management': 'vulnerability-management',
                'Identity & Access Security': 'identity-access-security',
                'Endpoint & Network Security': 'endpoint-network-security',
                'Data Protection & Backup': 'data-protection-backup',
                'Security Awareness & Training': 'security-awareness-training',
              }[video.service]}`}
              className="text-cyan-300 hover:text-cyan-200"
            >
              {video.service}
            </Link>
          </div>
        )}

        <div className="mt-4 flex flex-wrap gap-2">
          <Link
            to={`/the-cyber-guy/${video.slug || video.youtubeId}`}
            data-testid={`watch-video-${testKey}`}
            className="inline-flex items-center gap-2 px-4 py-2 text-xs sm:text-sm font-semibold rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:from-cyan-400 hover:to-blue-400 transition-colors"
          >
            <Play className="w-3.5 h-3.5" /> Watch Video
          </Link>
          <a
            href={watchUrl(video.youtubeId)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`watch-yt-${testKey}`}
            className="inline-flex items-center gap-2 px-4 py-2 text-xs sm:text-sm font-medium rounded-full border border-cyan-400/50 text-cyan-300 hover:bg-cyan-400/10 transition-colors"
          >
            <Youtube className="w-3.5 h-3.5" /> Watch on YouTube
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>
    </article>
  );
};

export default YouTubeVideoCard;
